import { useState } from "react";
import { Loader2, MapPin, Link as LinkIcon, X } from "lucide-react";
import toast from "react-hot-toast";
import { AdminOrderManagementService } from "../../../services/api/admin-order.service";
import { DoctorService } from "../../../services/api/doctor.service";
import LocationPicker, { type AddressDetails } from "../../../shared/GoogleMap";
import type { PatientAddress } from "../../../services/api/admin-order.types";

interface AddAddressFormProps {
  patientId: string;
  onAddressAdded: (address: PatientAddress) => void;
  onCancel: () => void;
}

const AddAddressForm: React.FC<AddAddressFormProps> = ({ patientId, onAddressAdded, onCancel }) => {
  const [form, setForm] = useState({
    address: "",
    addressName: "Home",
    city: "",
    state: "",
    pincode: "",
    landmark: "",
    latitude: 0,
    longitude: 0,
  });
  const [saving, setSaving] = useState(false);

  // Google Maps link state
  const [googleMapLink, setGoogleMapLink] = useState("");
  const [fetchingLink, setFetchingLink] = useState(false);

  // Map picker state
  const [showMap, setShowMap] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // ── Fetch from Google Maps Link ──────────────────────────
  const handleFetchFromLink = async () => {
    if (!googleMapLink.trim()) {
      toast.error("Please enter a Google Maps link");
      return;
    }
    setFetchingLink(true);
    try {
      const response = await DoctorService.getMapDetails(googleMapLink);
      if (response.success && response.data) {
        const { lat, long, address, city, state, pincode } = response.data;
        setForm((prev) => ({
          ...prev,
          latitude: parseFloat(lat) || 0,
          longitude: parseFloat(long) || 0,
          address: address || prev.address,
          city: city || prev.city,
          state: state || prev.state,
          pincode: pincode || prev.pincode,
        }));
        toast.success("Location details fetched successfully!");
      } else {
        toast.error(response.message || "Failed to fetch location details");
      }
    } catch {
      toast.error("An error occurred while fetching location details");
    } finally {
      setFetchingLink(false);
    }
  };

  // ── Handle Map Picker Confirm ────────────────────────────
  const handleLocationSelect = (lat: string, lng: string, addressDetails: AddressDetails) => {
    setForm((prev) => ({
      ...prev,
      latitude: parseFloat(lat) || 0,
      longitude: parseFloat(lng) || 0,
      address: addressDetails.fullAddress || prev.address,
      city: addressDetails.city || prev.city,
      state: addressDetails.state || prev.state,
      pincode: addressDetails.pincode || prev.pincode,
    }));
  };

  // ── Submit ───────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.address.trim() || !form.city.trim() || !form.state.trim() || !form.pincode.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);

    let lat = form.latitude;
    let lng = form.longitude;

    // If lat/lng not set, attempt geocoding from typed address
    if (lat === 0 && lng === 0 && window.google && window.google.maps && window.google.maps.Geocoder) {
      const fullAddr = `${form.address}, ${form.city}, ${form.state} ${form.pincode}`.trim();
      try {
        const geocoder = new window.google.maps.Geocoder();
        const geocodeResult = await new Promise<any>((resolve) => {
          geocoder.geocode({ address: fullAddr }, (results, status) => {
            if (status === "OK" && results?.[0]) resolve(results[0]);
            else resolve(null);
          });
        });
        if (geocodeResult?.geometry?.location) {
          lat = geocodeResult.geometry.location.lat();
          lng = geocodeResult.geometry.location.lng();
        }
      } catch (err) {
        console.warn("Auto-geocoding failed:", err);
      }
    }

    try {
      const res = await AdminOrderManagementService.addPatientAddress({
        patientId,
        address: form.address,
        state: form.state,
        city: form.city,
        pincode: form.pincode,
        isDefault: false,
        addressName: form.addressName,
        landmark: form.landmark,
        latitude: lat,
        longitude: lng,
      });
      if (res.success) {
        toast.success("Address added successfully!");
        onAddressAdded(res.data);
      } else {
        toast.error(res.message || "Failed to add address");
      }
    } catch {
      toast.error("Failed to add address");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[50] flex items-center justify-center p-4 animate-in" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white">
          <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Add New Address</h4>
          <button onClick={onCancel} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 bg-transparent border-none cursor-pointer transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto flex-1">
          {/* Google Maps Link Input */}
          <div className="mb-4">
            <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Google Maps Link</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <LinkIcon size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={googleMapLink}
                  onChange={(e) => setGoogleMapLink(e.target.value)}
                  placeholder="Paste Google Maps Link (e.g., https://maps.app.goo.gl/...)"
                  className="w-full pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white"
                />
              </div>
              <button
                onClick={handleFetchFromLink}
                disabled={fetchingLink}
                className="px-3 py-2 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap border-none cursor-pointer transition-colors flex items-center gap-1.5"
              >
                {fetchingLink ? <Loader2 size={12} className="animate-spin" /> : <LinkIcon size={12} />}
                {fetchingLink ? "Fetching..." : "Fetch"}
              </button>
              <button
                onClick={() => setShowMap(true)}
                className="px-3 py-2 text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-200 whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1.5"
              >
                <MapPin size={12} /> Pick on Map
              </button>
            </div>
            {/* Address */}
            <div className="mb-3">
              <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Full Address *</label>
              <input
                type="text"
                value={form.address}
                onChange={(e) => handleChange("address", e.target.value)}
                placeholder="Street address or detailed location"
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white"
              />
            </div>

            {/* City, State, Pincode */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">City *</label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => {
                    if (/^[A-Za-z\s]*$/.test(e.target.value)) handleChange("city", e.target.value);
                  }}
                  placeholder="City"
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white"
                />
              </div>
              <div>
                <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">State *</label>
                <input
                  type="text"
                  value={form.state}
                  onChange={(e) => {
                    if (/^[A-Za-z\s]*$/.test(e.target.value)) handleChange("state", e.target.value);
                  }}
                  placeholder="State"
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white"
                />
              </div>
              <div>
                <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Pincode *</label>
                <input
                  type="text"
                  value={form.pincode}
                  onChange={(e) => {
                    if (/^\d{0,6}$/.test(e.target.value)) handleChange("pincode", e.target.value);
                  }}
                  placeholder="Pincode"
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white"
                />
              </div>
            </div>

            {/* Landmark */}
            <div className="mb-3">
              <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Landmark (Optional)</label>
              <input
                type="text"
                value={form.landmark}
                onChange={(e) => handleChange("landmark", e.target.value)}
                placeholder="Near..."
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white"
              />
            </div>

            {/* Lat/Lng Preview */}
            {(form.latitude !== 0 || form.longitude !== 0) && (
              <div className="px-2 py-1.5 bg-slate-100 rounded-lg text-[0.65rem] text-slate-500 font-medium">
                📍 Lat: {form.latitude.toFixed(6)}, Lng: {form.longitude.toFixed(6)}
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-xs font-semibold border border-slate-200 bg-white text-slate-600 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 text-xs font-semibold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 cursor-pointer border-none transition-colors disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
            >
              {saving && <Loader2 size={12} className="animate-spin" />}
              {saving ? "Saving..." : "Save Address"}
            </button>
          </div>
        </div>

        {/* Google Map Picker Modal */}
        {showMap && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70]">
            <LocationPicker
              location={location}
              setLocation={setLocation}
              address={`${form.address} ${form.city} ${form.state} ${form.pincode}`.trim()}
              setShowMap={setShowMap}
              onLocationSelect={handleLocationSelect}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AddAddressForm;
